import ExcelJS from 'exceljs';
import buildReportFilename from './reportFilename';

const toCamelCase = (name) => {
  if (!name) return name;
  if ([...name].every(c => (c >= 'A' && c <= 'Z') || c === '_')) return name.toLowerCase();
  return name.charAt(0).toLowerCase() + name.slice(1);
};

// WM theme colors (matches importTemplateBuilder.js)
const THEME = {
  headerFill: 'FF1A5568',
  headerFont: 'FFFFFFFF',
  headerBorder: 'FF134452',
  titleColor: 'FF1A5568',
  zebraFill: 'FFF3F4F6',
  borderColor: 'FFE5E7EB'
};

/**
 * Generate and download an Excel report with WM branding.
 */
// Write one worksheet of data into a workbook
const writeSheet = (wb, sheetName, columns, sheetData, config, parameters) => {
  const ws = wb.addWorksheet(sheetName.replace(/[/\\?*[\]]/g, '').slice(0, 31));
  let currentRow = 1;

  const companyCell = ws.getCell(currentRow, 1);
  companyCell.value = config.branding?.companyName || 'Wunderlich Malec Engineering';
  companyCell.font = { name: 'Calibri', size: 14, bold: true, color: { argb: THEME.titleColor } };
  currentRow++;

  const titleCell = ws.getCell(currentRow, 1);
  titleCell.value = config.reportName;
  titleCell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: THEME.titleColor } };
  currentRow++;

  const dateCell = ws.getCell(currentRow, 1);
  dateCell.value = `Generated: ${new Date().toLocaleString()}`;
  dateCell.font = { name: 'Calibri', size: 9, italic: true, color: { argb: 'FF6B7280' } };
  currentRow++;

  if (parameters) {
    const paramParts = [];
    (config.parameters || []).forEach(param => {
      const sels = parameters[param.field] || [];
      if (sels.length > 0) {
        paramParts.push(`${param.label}: ${sels.map(s => s.fullDisplay || s.display).join(', ')}`);
      }
    });
    if (paramParts.length > 0) {
      const paramCell = ws.getCell(currentRow, 1);
      paramCell.value = `Filters: ${paramParts.join(' | ')}`;
      paramCell.font = { name: 'Calibri', size: 9, color: { argb: 'FF6B7280' } };
    }
  }
  currentRow++;
  currentRow++; // blank spacer

  const headerRowNum = currentRow;
  const headerRow = ws.getRow(headerRowNum);
  columns.forEach((col, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = col.label;
    cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: THEME.headerFont } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: THEME.headerFill } };
    cell.alignment = { horizontal: col.align || 'left', vertical: 'middle' };
    cell.border = { bottom: { style: 'thin', color: { argb: THEME.headerBorder } }, right: { style: 'thin', color: { argb: THEME.headerBorder } } };
  });
  headerRow.height = 22;
  ws.autoFilter = { from: { row: headerRowNum, column: 1 }, to: { row: headerRowNum, column: columns.length } };
  currentRow++;
  ws.views = [{ state: 'frozen', ySplit: headerRowNum, xSplit: 0 }];

  sheetData.forEach((row, idx) => {
    const dataRow = ws.getRow(currentRow);
    const isZebra = idx % 2 === 1;
    columns.forEach((col, i) => {
      const camelField = toCamelCase(col.field);
      const displayKey = `${camelField}_Display`;
      const displayValue = row[displayKey] != null ? row[displayKey] : row[camelField];
      const cell = dataRow.getCell(i + 1);
      cell.value = displayValue != null ? displayValue : '';
      cell.font = { name: 'Calibri', size: 10 };
      cell.alignment = { horizontal: col.align || 'left', vertical: 'middle' };
      cell.border = { bottom: { style: 'hair', color: { argb: THEME.borderColor } }, right: { style: 'hair', color: { argb: THEME.borderColor } } };
      if (isZebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: THEME.zebraFill } };
    });
    currentRow++;
  });

  columns.forEach((col, i) => {
    const camelField = toCamelCase(col.field);
    const displayKey = `${camelField}_Display`;
    let maxLen = col.label.length;
    sheetData.forEach(row => {
      const val = row[displayKey] != null ? row[displayKey] : row[camelField];
      if (val != null) maxLen = Math.max(maxLen, String(val).length);
    });
    ws.getColumn(i + 1).width = Math.min(Math.max(maxLen + 2, 8), 50);
  });
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const exportReportExcel = async ({
  config,
  columns,
  data,
  parameters,
  groupByField,
  autoDownload = false,
  isCancelled,
  onProgress
}) => {
  try {
    if (onProgress) onProgress('Generating Excel...');

    const wb = new ExcelJS.Workbook();
    wb.creator = config.branding?.companyName || 'Wunderlich Malec Engineering';
    wb.created = new Date();

    if (groupByField) {
      // Multiple sheets — one per unique group value
      const groupMap = new Map();
      data.forEach(row => {
        const cf = toCamelCase(groupByField);
        const val = row[`${cf}_Display`] != null ? row[`${cf}_Display`] : row[cf];
        const key = val != null ? String(val) : '(Blank)';
        if (!groupMap.has(key)) groupMap.set(key, []);
        groupMap.get(key).push(row);
      });
      [...groupMap.entries()]
        .sort(([a], [b]) => {
          if (a === '(Blank)') return 1;
          if (b === '(Blank)') return -1;
          return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
        })
        .forEach(([groupVal, groupRows]) => {
          writeSheet(wb, groupVal, columns, groupRows, config, parameters);
        });
    } else {
      writeSheet(wb, config.reportName, columns, data, config, parameters);
    }

    const buffer = await wb.xlsx.writeBuffer();
    if (isCancelled && isCancelled()) { if (onProgress) onProgress(null); return false; }

    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const filename = buildReportFilename(config.reportName, 'xlsx');

    if (!autoDownload && window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [{ description: 'Excel Workbook', accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] } }]
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        if (onProgress) onProgress(null);
        return true;
      } catch (err) {
        if (err.name === 'AbortError') { if (onProgress) onProgress(null); return false; }
      }
    }

    downloadBlob(blob, filename);
    if (onProgress) onProgress(null);
    return true;
  } catch (err) {
    console.error('[reportExcelExporter] Error:', err);
    if (onProgress) onProgress(null);
    throw err;
  }
};

export default exportReportExcel;
